import type { AnswerGroupContext } from './answerGroupSummaries';
import { createAnswerGroup } from './buildAnswerGroups';
import type { AnswerGroup } from './types';

export const CURRENT_REPORT_GROUP_ID = 'comparison:current-report';
export const WHOLE_REPORT_GROUP_ID = 'comparison:whole-report';

/** Live baselines follow current filters; saved groups are resolved separately from their captured rules. */
export function createComparisonGroupSelector(
  context: AnswerGroupContext,
  tree: AnswerGroup[],
  reportLabel: string,
  reportGroupId: string = CURRENT_REPORT_GROUP_ID,
) {
  const cache = new WeakMap<AnswerGroup[], Map<string, AnswerGroup>>();
  return (filteredGroups: AnswerGroup[]): Map<string, AnswerGroup> => {
    const cached = cache.get(filteredGroups);
    if (cached) return cached;
    const eligible = new Set<string>();
    const responses = new Set<string>();
    filteredGroups.forEach((group) => {
      group.respondentIds.forEach((id) => eligible.add(id));
      group.responseIds.forEach((id) => responses.add(id));
    });
    const groups = new Map<string, AnswerGroup>();
    const visit = (group: AnswerGroup): AnswerGroup => {
      const children = group.children.map(visit);
      const unchanged =
        [...group.respondentIds].every((id) => eligible.has(id)) &&
        [...group.responseIds].every((id) => responses.has(id));
      const filtered = unchanged
        ? group
        : createAnswerGroup(context, {
            id: group.id,
            label: group.label,
            type: group.type,
            teamId: group.teamId,
            teamNames: group.teamNames,
            directRespondentIds: [...group.directRespondentIds].filter((id) => eligible.has(id)),
            directResponseIds: [...group.directResponseIds].filter((id) => responses.has(id)),
            children: children.filter((child) => child.respondentCount > 0),
          });
      // Keep zero-member teams selectable instead of dropping them when filters exclude their members.
      groups.set(group.id, filtered);
      return filtered;
    };
    tree.forEach(visit);
    const singleGroup = filteredGroups.length === 1 ? filteredGroups[0] : undefined;
    // Keep the single-group drill-down shape without borrowing its team/person identity.
    // The factory derives aggregate team names from membership in either case.
    const currentReport = createAnswerGroup(context, {
      id: reportGroupId,
      label: reportLabel,
      type: 'root',
      directRespondentIds: singleGroup?.directRespondentIds,
      directResponseIds: singleGroup?.directResponseIds,
      children: singleGroup?.children ?? filteredGroups,
    });
    groups.set(reportGroupId, currentReport);
    cache.set(filteredGroups, groups);
    return groups;
  };
}
